import React, { useEffect, useState, useMemo } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  FormHelperText,
  Checkbox,
  ListItemText,
  Input,
  Chip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import axios from 'axios';
import { useApi, identityApiRef, discoveryApiRef } from '@backstage/core-plugin-api';
import yaml from 'js-yaml';

const useStyles = makeStyles({
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
});

export const SelectUndeployApps = (props: FieldExtensionComponentProps<string[]>) => {
  const { onChange, formData, required, rawErrors, schema, formContext } = props;
  const classes = useStyles();

  // Apps live under the Backstage project (`project_name`), not the ADO project —
  // the SSM key is /backstage/project/{project_name}/{product}.
  const resolvedProjectName = useMemo(() => {
    // Try to get projectName from various possible locations
    const projectName =
      formContext.formData?.projectName ||
      (formContext as any).rootFormData?.projectName ||
      '';

    console.log('SelectUndeployApps: resolved projectName =', projectName);
    return projectName;
  }, [formContext]);

  const resolvedProduct = useMemo(() => {
    // The product field is in the same object as apps (the current array item)
    // So we need to look at the parent formData or idSchema to find it
    
    // Try multiple approaches to get the product value
    let product = '';
    
    // Approach 1: Check if there's a product field at the same level
    if (props.idSchema && props.idSchema.$id) {
      // Parse the id to find the parent (array item)
      // ID format is like: root_undeployItems_0_apps
      const idParts = props.idSchema.$id.split('_');
      if (idParts.length >= 3) {
        // Get the array index (e.g., "0" from root_undeployItems_0_apps)
        const arrayIndex = idParts[idParts.length - 2];
        const arrayPath = idParts.slice(1, -1).join('.');
        
        // Try to get the product from the parent array item
        const pathParts = arrayPath.split('.');
        let current: any = formContext.formData;
        for (const part of pathParts.slice(0, -1)) {
          if (current && current[part]) {
            current = current[part];
          }
        }
        if (current && Array.isArray(current[pathParts[pathParts.length - 1]])) {
          const arrayItems = current[pathParts[pathParts.length - 1]];
          const index = parseInt(arrayIndex, 10);
          if (!isNaN(index) && arrayItems[index] && arrayItems[index].product) {
            product = arrayItems[index].product;
          }
        }
      }
    }
    
    // Approach 2: Fallback - check formContext for undeployItems
    if (!product && formContext.formData?.undeployItems) {
      // Find which array item we're in by checking all items
      const items = formContext.formData.undeployItems;
      if (Array.isArray(items)) {
        // Try to find the item that matches our current context
        // This is a bit hacky but should work for most cases
        for (const item of items) {
          if (item && item.product) {
            product = item.product;
            break; // Use the first one for now
          }
        }
      }
    }
    
    console.log('SelectUndeployApps: resolved product =', product);
    console.log('SelectUndeployApps: idSchema =', props.idSchema);
    return product;
  }, [formContext, props.idSchema]);

  const endpoint = `/backstage/project/${resolvedProjectName}/${resolvedProduct}`;

  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const identityApi = useApi(identityApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  useEffect(() => {
    // Reset options and fetch error.
    setOptions([]);
    setFetchError(null);

    // If no project or product is selected, skip fetching.
    if (!resolvedProjectName || !resolvedProduct) {
      setLoading(false);
      setFetchError('Please select a valid Project Name and Product');
      return;
    }

    setLoading(true);
    async function fetchApps() {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
        const credentials = await identityApi.getCredentials();
        const token = credentials.token;
        const response = await axios.post(
          `${baseUrl}/aws/params`,
          { name: endpoint },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        const rawValue = response.data.value || '';
        if (!rawValue) throw new Error('Invalid response: No value returned');

        const parsed = yaml.load(rawValue);
        let appList: string[] = [];
        
        if (parsed && typeof parsed === 'object' && Array.isArray((parsed as any).apps)) {
          appList = (parsed as any).apps.map((app: any) => {
            if (typeof app === 'string') return app;
            if (app && typeof app === 'object' && app.name) return app.name;
            return null;
          }).filter((a: string | null) => a !== null);
        } else if (typeof parsed === 'string') {
          appList = parsed.split(',').map((a: string) => a.trim()).filter((a: string) => a);
        } else {
          throw new Error('Apps list not found in response');
        }

        setOptions(appList);
        setFetchError(null);
      } catch (err: any) {
        console.error('Error fetching apps for undeploy:', err);
        setFetchError('Failed to fetch apps. Please try again.');
      } finally {
        setLoading(false);
      }
    }

    fetchApps();
  }, [resolvedProjectName, resolvedProduct, endpoint, identityApi, discoveryApi]);

  const handleChange = (event: any) => {
    const value = event.target.value;
    onChange(typeof value === 'string' ? value.split(',') : value);
  };

  const selectedApps = Array.isArray(formData) ? formData : [];

  return (
    <FormControl fullWidth required={required} error={!!rawErrors?.length || !!fetchError}>
      <InputLabel>{schema.title}</InputLabel>
      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <>
          <Select
            multiple
            value={selectedApps}
            onChange={handleChange}
            input={<Input />}
            renderValue={(selected) => (
              <div className={classes.chipContainer}>
                {(selected as string[]).map((value) => (
                  <Chip key={value} label={value} />
                ))}
              </div>
            )}
            disabled={!resolvedProjectName || !resolvedProduct}
          >
            {options.map((option) => (
              <MenuItem key={option} value={option}>
                <Checkbox checked={selectedApps.indexOf(option) > -1} />
                <ListItemText primary={option} />
              </MenuItem>
            ))}
          </Select>
          {fetchError && <FormHelperText>{fetchError}</FormHelperText>}
          {rawErrors && rawErrors.length > 0 && (
            <FormHelperText>{rawErrors.join(', ')}</FormHelperText>
          )}
        </>
      )}
    </FormControl>
  );
};

